import { service } from '@ember/service'
import Component from '@glimmer/component'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import { range as _range } from 'lodash'
import { tracked } from '@glimmer/tracking'
import { t, type IntlService } from 'ember-intl'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type Student from 'teacher-ui-client/models/student'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import type { TOC } from '@ember/component/template-only'
import {
  InlineSegmentedBar,
  SegmentedAxis,
  type AxisRangeItem,
  type RangeItem,
} from 'teacher-ui-client/components/giraffe/primitives/inline-segmented-bar'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import SortColumnIcon from 'teacher-ui-client/components/sort-column-icon'
import IsSubco from 'teacher-ui-client/helpers/is-subco'
import { and } from 'ember-truth-helpers'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import type RouterService from '@ember/routing/router-service'
import type Owner from '@ember/owner'
import type { StorylandsHistoryData } from 'teacher-ui-client/utils/student-events/fetchers/storylands-history'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface HistoryDataViewModel {
  firstName: string
  lastName: string
  teacherName: string
  gradePosition: number
  totalAttempts: number | undefined
  studentId: string
  maxLesson: number | undefined
  series: RangeItem[]
}

interface Args {
  studentRouteName: string
  themeKey: ThemeKeys
  uiScope: UIScopeName
  students: Student[]
  data: StorylandsHistoryData
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class StorylandsLessonsHistory extends Component<Signature> {
  @tracked sortingPriorityQueue = []

  @service router!: RouterService

  @service intl!: IntlService

  processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get studentData() {
    return this.processor.transformedData
  }

  get historyData() {
    return this.args.data ?? []
  }

  get lessonNumbers() {
    return this.historyData.flatMap((studentData) => studentData.lessons.map((lesson) => lesson.lesson))
  }

  get range() {
    return _range(Math.min(...this.lessonNumbers) ?? 1, (Math.max(...this.lessonNumbers) ?? 1) + 1)
  }

  get rangeMin() {
    return this.range[0]
  }

  get rangeMax() {
    return this.range[this.range.length - 1]
  }

  get axisRange(): AxisRangeItem[] {
    return this.range.map((lessonNumber) => {
      /*
       * Show the label
       *  - if the lesson number is the first or last lesson
       *  - if the lesson is divisble by 5
       */
      const mod = lessonNumber % 5
      const showLabel = mod === 0 || lessonNumber === this.rangeMin || lessonNumber === this.rangeMax
      return {
        label: showLabel ? String(lessonNumber) : undefined,
        isLast: lessonNumber === this.rangeMax,
      }
    })
  }

  get data(): HistoryDataViewModel[] {
    return this.args.students.filter(hasId).map((student) => {
      const studentData = this.historyData.find((data) => String(data.student_id) === student.id)
      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName: student.readingTeacherNames.join(', '),
        gradePosition: student.gradePosition,
        totalAttempts: studentData?.total_attempts,
        maxLesson: studentData?.max_lesson,
        studentId: student.id,
        series: this.range.map<RangeItem>((lessonNumber) => {
          const lessonData = studentData?.lessons.find((lesson) => lesson.lesson === lessonNumber)

          // Show border if the lesson number isn't the first or last lesson and the lesson is divisble by 5
          const mod = lessonNumber % 5
          const showBorder = mod === 0 && lessonNumber !== this.rangeMin && lessonNumber !== this.rangeMax

          if (!lessonData) {
            return {
              bgColor: 'bg-transparent',
              showBorder,
            }
          }

          const averageScore = lessonData.average_score
          const tooltip = this.intl.t('reporting.tooltips.storylandsLessonsHistory.chartItem.lessonTooltip', {
            lessonNumber,
            averageScore,
          })

          return {
            bgColor: 'bg-juicy-orange-300',
            showBorder,
            tooltip,
          }
        }),
      }
    })
  }

  get isSchoolScope() {
    return this.args.uiScope === 'school'
  }

  get showCharts() {
    return this.args.uiScope !== 'school'
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo(this.args.studentRouteName, studentId)
  }

  <template>
    <div
      data-test-giraffe-storylands-history
      class="border-dusty-black-50 shadow-dusty-black-100 relative mb-6 rounded-sm border shadow-md"
      ...attributes
    >
      {{#let this.processor.transformedData as |transformedData|}}
        <table class="w-full overflow-hidden rounded-sm text-sm">
          <thead class="bg-juicy-orange-300">
            <tr class="divide-x divide-white">
              <TH class="w-36" {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}>
                {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                <SortColumnIcon
                  class="ml-2"
                  @sortingPriorityQueue={{this.sortingPriorityQueue}}
                  @columnName="firstName"
                />
              </TH>
              <TH class="w-36" {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}>
                {{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                <SortColumnIcon
                  class="ml-2"
                  @sortingPriorityQueue={{this.sortingPriorityQueue}}
                  @columnName="lastName"
                />
              </TH>
              {{#if (IsSubco)}}
                <TH
                  data-test-teacher-name-header
                  {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}
                >
                  {{t "reporting.dashboardTables.teacher"}}
                  <SortColumnIcon
                    class="ml-2"
                    @sortingPriorityQueue={{this.sortingPriorityQueue}}
                    @columnName="teacherName"
                  />
                </TH>
                <TH
                  data-test-grade-position-header
                  {{on "click" (fn this.processor.tableActions.sortByNumber "gradePosition")}}
                >
                  {{t "grade.label"}}
                  <SortColumnIcon
                    class="ml-2"
                    @sortingPriorityQueue={{this.sortingPriorityQueue}}
                    @columnName="gradePosition"
                  />
                </TH>
              {{/if}}
              <TH {{on "click" (fn this.processor.tableActions.sortByNumber "maxLesson")}}>
                {{t "reporting.charts.storylandsLessons.highestLessonCompleted" htmlSafe=true}}
                <SortColumnIcon
                  class="ml-2"
                  @sortingPriorityQueue={{this.sortingPriorityQueue}}
                  @columnName="maxLesson"
                />
              </TH>
              <TH {{on "click" (fn this.processor.tableActions.sortByValue "totalAttempts")}}>
                {{t "reporting.charts.storylandsLessons.totalAttempts" htmlSafe=true}}
                <SortColumnIcon
                  class="ml-2"
                  @sortingPriorityQueue={{this.sortingPriorityQueue}}
                  @columnName="totalAttempts"
                />
              </TH>
              {{#if (and this.showCharts @students)}}
                <th
                  class="text-dusty-black-500 relative border-none
                    {{if (IsSubco) 'w-1/3' 'w-1/2'}}
                    bg-white print:hidden"
                >
                  <h3 class="mt-2 px-2">{{t "reporting.chartTitles.storylandsHistory"}}</h3>
                  <SegmentedAxis @axisRange={{this.axisRange}} />
                </th>
              {{/if}}
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 print:border-dusty-black-100 cursor-pointer divide-y-2 print:border-t">
            {{#each transformedData as |row index|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr data-test-student-row={{index}} {{on "click" (fn this.transitionToStudentRoute row.studentId)}}>
                <TD class="text-left">
                  {{row.firstName}}
                </TD>
                <TD class="text-left">
                  {{row.lastName}}
                </TD>
                {{#if (IsSubco)}}
                  <TD data-test-teacher-name-row class="text-left">
                    {{row.teacherName}}
                  </TD>
                  <TD data-test-grade-position-row class="text-center">
                    {{gradeName row.gradePosition}}
                  </TD>
                {{/if}}
                <TD class="text-center">
                  {{formatValue row.maxLesson}}
                </TD>
                <TD data-test-total-attempts class="text-center">
                  {{formatValue row.totalAttempts}}
                </TD>
                {{#if (and this.showCharts @students)}}
                  <td class="border-dusty-black-50 h-20 border-l print:hidden">
                    <InlineSegmentedBar class="h-full" @series={{row.series}} />
                  </td>
                {{/if}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      {{/let}}
    </div>
  </template>
}

export default StorylandsLessonsHistory

interface CellSignature {
  Element: HTMLTableCellElement
  Blocks: {
    default: []
  }
}

const TH: TOC<CellSignature> = <template>
  <th class="rounded-tl-md px-4 py-3 text-left font-normal text-wrap" role="button" ...attributes>
    <div class="flex items-center justify-between text-left align-middle text-white print:text-black">
      {{yield}}
    </div>
  </th>
</template>

const TD: TOC<CellSignature> = <template>
  <td class="h-20 px-2 py-0" ...attributes>
    {{yield}}
  </td>
</template>
